import { FC, useContext } from 'react';

import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';

interface CounterProps {
    counter: number;
    setCounter: React.Dispatch<React.SetStateAction<number>>;
    analyticsPrefix?: string;
    min?: number;
    max?: number;
}

const OutlinedIconButton = styled(IconButton)(({ theme }) => ({
    width: theme.spacing(4),
    height: theme.spacing(4),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    '&:disabled': {
        borderColor: theme.palette.purple?.[100],
        color: theme.palette.purple?.[100],
    },
}));

const Counter: FC<CounterProps> = ({ counter, setCounter, analyticsPrefix, min = 1, max = 5 }) => {
    const { spacing } = useTheme();
    const { trackEvent } = useContext(AnalyticsContext);

    return (
        <Stack direction="row" sx={{ alignItems: 'center' }}>
            <OutlinedIconButton
                aria-label="subtract"
                onClick={() => {
                    trackEvent('Counter Clicked', {
                        objectType: ObjectTypes.Button,
                        context: analyticsPrefix,
                        counterValue: counter,
                        buttonPressed: '-',
                    });

                    setCounter((prev) => Math.max(prev - 1, min));
                }}
                size="small"
                disabled={counter <= min}
                disableRipple
            >
                <FontAwesomeIcon icon={faMinus} />
            </OutlinedIconButton>

            <Typography
                variant="h3"
                sx={{
                    width: spacing(2.5),
                    mx: spacing(1.5),
                    textAlign: 'center',
                    fontWeight: 'bold',
                }}
            >
                {counter}
            </Typography>

            <OutlinedIconButton
                aria-label="add"
                onClick={() => {
                    trackEvent('Counter Clicked', {
                        objectType: ObjectTypes.Button,
                        context: analyticsPrefix,
                        counterValue: counter,
                        buttonPressed: '+',
                    });

                    setCounter((prev) => Math.min(prev + 1, max));
                }}
                size="small"
                disabled={counter >= max}
                disableRipple
            >
                <FontAwesomeIcon icon={faPlus} />
            </OutlinedIconButton>
        </Stack>
    );
};

export default Counter;
